/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import {
	createContext,
	useContext,
	forwardRef,
	type CSSProperties,
	type ImgHTMLAttributes,
	type ComponentType,
} from 'react'

/* ----------------------------- Image ----------------------------- */

type FillLayout = {
	fill: true
	/** Strongly recommended when using fill */
	sizes?: string
}

type FixedLayout = {
	width: number
	height: number
	fill?: false
}

/** Next/Image extras we want to allow on adapters (all optional here) */
type NextImageExtras = {
	priority?: boolean
	placeholder?: 'blur' | 'empty'
	blurDataURL?: string
	quality?: number
	unoptimized?: boolean
	onLoadingComplete?: (img: HTMLImageElement) => void
}

export type MDSImageProps =
	// Core, DS-neutral props
	({
		alt: string
		/** DS-neutral; Next adapter may also accept StaticImageData at runtime */
		src: string
		className?: string
		style?: CSSProperties
		loading?: 'eager' | 'lazy'
		decoding?: 'sync' | 'async' | 'auto'
		fetchPriority?: 'high' | 'low' | 'auto'
		objectFit?: string
		objectPosition?: string
	} & (FillLayout | FixedLayout)) &
		// Allow common <img> props without clobbering the ones we define
		Omit<
			ImgHTMLAttributes<HTMLImageElement>,
			'src' | 'alt' | 'width' | 'height' | 'loading' | 'decoding'
		> &
		// Make Next extras available (adapters can use them; default img ignores)
		NextImageExtras

type ImageComponent = ComponentType<MDSImageProps>

const DefaultImg: ImageComponent = (props) => {
	const {
		alt,
		src,
		className,
		style,
		loading,
		decoding,
		fetchPriority,
		objectFit,
		objectPosition,
		// Next extras are accepted but ignored by the default renderer
	} = props

	if ('fill' in props && props.fill) {
		return (
			<img
				alt={alt}
				src={src}
				className={className}
				loading={loading}
				decoding={decoding}
				fetchPriority={fetchPriority}
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
					objectFit: (objectFit as CSSProperties['objectFit']) ?? 'cover',
					objectPosition,
					...style,
				}}
			/>
		)
	}

	return (
		<img
			alt={alt}
			src={src}
			width={props.width}
			height={props.height}
			className={className}
			loading={loading}
			decoding={decoding}
			fetchPriority={fetchPriority}
			style={{
				objectFit: objectFit as CSSProperties['objectFit'],
				objectPosition,
				...style,
			}}
		/>
	)
}

const ImageCtx = createContext<ImageComponent>(DefaultImg)

/* ------------------------------ Link ----------------------------- */

/**
 * Start from all standard <a> props, then refine:
 * - force a typed href
 * - keep children required
 * - keep Next-specific props optional so the default <a> can ignore them
 */
export type MDSLinkProps = Omit<
	React.AnchorHTMLAttributes<HTMLAnchorElement>,
	'href'
> & {
	href: string | URL
	target?: React.HTMLAttributeAnchorTarget
	onClick?: React.MouseEventHandler<HTMLAnchorElement>
} & {
	// Next.js extras (optional; default <a> ignores them)
	prefetch?: boolean
	replace?: boolean
	scroll?: boolean
	shallow?: boolean
	locale?: string | false
}

type LinkComponent = React.ForwardRefExoticComponent<
	MDSLinkProps & React.RefAttributes<HTMLAnchorElement>
>

const DefaultLink: LinkComponent = forwardRef<HTMLAnchorElement, MDSLinkProps>(
	({ href, children, ...rest }, ref) => (
		<a
			ref={ref}
			href={typeof href === 'string' ? href : href.toString()}
			{...rest}
		>
			{children}
		</a>
	),
)

DefaultLink.displayName = 'DefaultLink'

const LinkCtx = createContext<LinkComponent>(DefaultLink)

/* ----------------------- Provider + hooks ------------------------ */

export function MDSProvider({
	children,
	imageComponent,
	linkComponent,
}: {
	children: React.ReactNode
	imageComponent?: ImageComponent
	linkComponent?: LinkComponent
}) {
	return (
		<ImageCtx.Provider value={imageComponent ?? DefaultImg}>
			<LinkCtx.Provider value={linkComponent ?? DefaultLink}>
				{children}
			</LinkCtx.Provider>
		</ImageCtx.Provider>
	)
}

export function useMDSImage() {
	return useContext(ImageCtx)
}

export function useMDSLink() {
	return useContext(LinkCtx)
}
